/**
 * The Watch page (`/w/:slug/watch?t=<watch token>`): where a registrant lands. The token comes off the address on
 * arrival and this browser keeps it (watchToken.ts), so no measurement tag ever sees it. A talk that plays any time
 * opens the recorded room (`room/RoomPage`); a live opens its live room.
 *
 * The private chat thread (`useWatchThread`) lives here too: the rooms and the living page all talk through it.
 */
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import type {
  Attachment,
  PublicMessageRequest,
  WatchMessage,
  WatchPageResponse,
  WatchSession,
} from "../../shared/types";
import { ApiError, api } from "../lib/api";
import { TroublePage, troubleOf } from "./TroublePage";
import type { TroubleKind } from "../../shared/trouble";
import { useViewportCover } from "./smallScreen";
import { forgetWatchToken, takeWatchToken } from "./watchToken";
import { LivePage } from "./live/LivePage";
import { RoomPage } from "./room/RoomPage";

/** Ten lines, then the field scrolls instead of growing. */
const GROW_MAX_PX = 240;
/** How many files may ride on one message. */
export const ATTACH_MAX = 5;
/** The kinds a host is willing to open, as the picker offers them. */
export const ATTACH_ACCEPT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt";

// ── helpers ─────────────────────────────────────────────────────────────
/** The talk this address is about, or nothing when the token has no such session. */
export function sessionForSlug(sessions: WatchSession[], slug: string): WatchSession | null {
  return sessions.find((s) => s.slug === slug) ?? null;
}

/** Anything the host has sent since, folded in without disturbing what is there. */
export function mergeMessages(have: WatchMessage[], fresh: WatchMessage[]): WatchMessage[] {
  const known = new Set(have.map((m) => m.id));
  const added = fresh.filter((m) => !known.has(m.id));
  if (added.length === 0) return have;
  return [...have, ...added].toSorted((a, b) => a.created_at - b.created_at);
}

export function humanSize(bytes: number): string {
  if (bytes < 1_048_576) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

// ── the route ───────────────────────────────────────────────────────────
export function WatchPageView(): ReactNode {
  useViewportCover();
  const { slug = "" } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const [token] = useState(() => takeWatchToken(slug, search.get("t")));
  const [data, setData] = useState<WatchPageResponse | null>(null);
  const [lost, setLost] = useState(false);
  const [trouble, setTrouble] = useState<{ kind: TroubleKind; origin?: string } | null>(null);

  const alive = useRef(true);
  const load = useCallback(
    () =>
      api.watch
        .page(token)
        .then((res) => {
          if (!alive.current) return;
          setTrouble(null);
          if (sessionForSlug(res.sessions, slug)) setData(res);
          else {
            forgetWatchToken(slug);
            setLost(true);
          }
        })
        .catch((err: unknown) => {
          if (!alive.current) return;
          const t = troubleOf(err);
          // A token nobody knows sends them to the form; anything else is said.
          if (t.kind === "seat") {
            forgetWatchToken(slug);
            setLost(true);
          } else setTrouble(t);
        }),
    [token, slug],
  );
  useEffect(() => {
    alive.current = true;
    if (token) void load();
    return () => {
      alive.current = false;
    };
  }, [token, load]);

  // A series moved on to another session: that session's page takes over.
  const moveOn = useCallback((fresh: WatchSession) => {
    setData((d) =>
      d ? { ...d, sessions: d.sessions.map((s) => (s.webinar_id === fresh.webinar_id ? fresh : s)) } : d,
    );
  }, []);

  // No token, a token nobody knows, or a token with no place on this talk: the
  // page with the form is where they belong.
  if (!token || lost) return <Navigate to={`/w/${slug}`} replace />;
  if (trouble) return <TroublePage kind={trouble.kind} values={{ origin: trouble.origin }} onAgain={load} />;
  const session = data ? sessionForSlug(data.sessions, slug) : null;
  if (!data || !session) {
    return (
      <div className="gst" style={{ display: "grid", placeItems: "center" }}>
        <span className="spin" aria-label="Loading" />
      </div>
    );
  }
  if (session.live) {
    return (
      <LivePage
        key={`${session.webinar_id}:${session.live.sessionId}`}
        token={token}
        data={data}
        session={session}
        live={session.live}
        onSession={moveOn}
      />
    );
  }
  return <RoomPage key={session.webinar_id} token={token} data={data} session={session} />;
}

/** The workspace's logo, or its two letters on its colour. */
export function BrandCrest({ brand }: { brand: Pick<WatchPageResponse["brand"], "logo_url" | "workspace_name" | "crest"> }): ReactNode {
  return brand.logo_url ? (
    <img className="w-brand-crest w-brand-logo" src={brand.logo_url} alt={brand.workspace_name} />
  ) : (
    <div className="w-brand-crest">
      <span>{brand.crest}</span>
    </div>
  );
}

// ── the chat ────────────────────────────────────────────────────────────
/** One file the viewer has picked but not sent yet. */
interface Pending {
  id: string;
  file: File;
  /** A preview address for a picture; null for everything else. */
  preview: string | null;
}

/**
 * Everything the chat carries: the thread, the draft, the files waiting to go.
 * It is held by whoever renders the chat, not by the chat, so the room can lift
 * the card into its theatre and back without losing a word of it.
 */
export interface WatchThread {
  messages: WatchMessage[];
  text: string;
  setText: (value: string) => void;
  queue: Pending[];
  trouble: string;
  sending: boolean;
  field: RefObject<HTMLTextAreaElement | null>;
  picker: RefObject<HTMLInputElement | null>;
  grow: () => void;
  pick: (files: FileList | File[] | null) => void;
  drop: (id: string) => void;
  send: () => void;
  /** The host is typing right now (from the poll). */
  hostTyping: boolean;
  /** Throttled "I'm typing" ping — call it from the field's onChange. */
  noteTyping: () => void;
}

export function useWatchThread(
  token: string,
  session: WatchSession,
  /** Called on every remote "refresh": the page can renew more than the chat. */
  onRemote?: () => void,
  /** What a send carries beside its words, read at the moment it goes: a live's room adds its session, a recording its playhead. */
  stamp?: () => Pick<PublicMessageRequest, "live_session_id" | "at_sec">,
): WatchThread {
  const [messages, setMessages] = useState<WatchMessage[]>(session.messages);
  const [hostTyping, setHostTyping] = useState(false);
  const typingLast = useRef(0);
  const [text, setText] = useState("");
  const [queue, setQueue] = useState<Pending[]>([]);
  const [trouble, setTrouble] = useState("");
  const [sending, setSending] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);

  // The live wire: a WebSocket into this conversation's room. "refresh"
  // refetches the thread; "typing" lights the dots; a (re)connect syncs once
  // so nothing is missed while the wire was down. No polling.
  const wire = useRef<WebSocket | null>(null);
  const typingOff = useRef<number | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    let retry: number | undefined;
    // Degraded mode: a slow poll only after three straight failed connects,
    // stopped the moment a socket opens. A normal browser never polls.
    let fails = 0;
    let slow: number | undefined;
    const ask = () => {
      api.watch
        .page(token)
        .then((res) => {
          if (!alive) return;
          const fresh = res.sessions.find((s) => s.webinar_id === session.webinar_id);
          if (fresh) setMessages((have) => mergeMessages(have, fresh.messages));
        })
        .catch(() => {});
    };
    const connect = () => {
      if (!alive) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(
        `${proto}://${location.host}/api/watch/${encodeURIComponent(token)}/ws?webinar_id=${encodeURIComponent(session.webinar_id)}`,
      );
      wire.current = ws;
      ws.onopen = () => {
        fails = 0;
        window.clearInterval(slow);
        slow = undefined;
        ask();
      };
      ws.onmessage = (e) => {
        let ev: { type?: string; from?: string };
        try {
          ev = JSON.parse(String(e.data));
        } catch {
          return;
        }
        if (ev.type === "refresh") {
          ask();
          onRemote?.();
        }
        if (ev.type === "typing" && ev.from === "host") {
          setHostTyping(true);
          window.clearTimeout(typingOff.current);
          typingOff.current = window.setTimeout(() => setHostTyping(false), 6_000);
        }
      };
      ws.onclose = () => {
        if (wire.current === ws) wire.current = null;
        if (!alive) return;
        fails += 1;
        if (fails >= 3 && slow === undefined) {
          slow = window.setInterval(() => {
            if (document.visibilityState === "visible") ask();
          }, 30_000);
        }
        retry = window.setTimeout(connect, 3_000);
      };
    };
    connect();
    return () => {
      alive = false;
      window.clearTimeout(retry);
      window.clearInterval(slow);
      window.clearTimeout(typingOff.current);
      wire.current?.close();
      wire.current = null;
    };
  }, [token, session.webinar_id, onRemote]);

  // Whatever is still waiting when the page goes is let go of.
  const waiting = useRef<Pending[]>([]);
  waiting.current = queue;
  useEffect(() => () => {
    for (const item of waiting.current) if (item.preview) URL.revokeObjectURL(item.preview);
  }, []);

  const grow = useCallback(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = "auto";
    // A field in a pane that is not showing measures 0: it keeps its own height until it shows.
    if (el.scrollHeight === 0) {
      el.style.height = "";
      return;
    }
    el.style.height = `${Math.min(GROW_MAX_PX, el.scrollHeight)}px`;
  }, []);

  const pick = (files: FileList | File[] | null) => {
    if (!files) return;
    setTrouble("");
    setQueue((have) =>
      [
        ...have,
        ...Array.from(files).map((file) => ({
          id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
          file,
          preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        })),
      ].slice(0, ATTACH_MAX),
    );
    if (picker.current) picker.current.value = "";
  };

  const drop = (id: string) =>
    setQueue((have) => {
      const going = have.find((q) => q.id === id);
      if (going?.preview) URL.revokeObjectURL(going.preview);
      return have.filter((q) => q.id !== id);
    });

  const send = async () => {
    const body = text.trim();
    if (sending || (!body && queue.length === 0)) return;
    setSending(true);
    setTrouble("");
    try {
      const attachments: Attachment[] = [];
      for (const item of queue) {
        const asset = await api.watch.upload(token, item.file);
        attachments.push({
          kind: asset.kind === "image" ? "image" : "file",
          name: asset.name,
          size: asset.size,
          key: asset.key,
        });
      }
      const sent = await api.watch.message(token, {
        webinar_id: session.webinar_id,
        body,
        ...(attachments.length > 0 ? { attachments } : {}),
        ...stamp?.(),
      });
      setMessages((have) => [...have, sent]);
      for (const item of queue) if (item.preview) URL.revokeObjectURL(item.preview);
      setQueue([]);
      setText("");
      const el = field.current;
      if (el) el.style.height = "";
    } catch (err) {
      setTrouble(err instanceof ApiError ? err.message : "That did not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const noteTyping = useCallback(() => {
    const ws = wire.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - typingLast.current < 2_500) return;
    typingLast.current = now;
    try {
      ws.send(JSON.stringify({ type: "typing" }));
    } catch {
      // the wire reconnects on its own
    }
  }, []);

  return { messages, text, setText, queue, trouble, sending, field, picker, grow, pick, drop, send: () => void send(), hostTyping, noteTyping };
}
